import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FileText,
  CreditCard,
  Plane,
  GraduationCap,
  HeartHandshake,
  ArrowDown,
  ArrowLeft,
} from 'lucide-react';
import Layout from '../layout/Layout';
import ScholarshipForm from '../components/scholarshipApplication/ScholarshipForm';
import Button from '../components/Button';

export default function Scholarship() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash !== '#apply') return;

    const target = document.getElementById('apply');
    if (!target) return;

    const headerOffset = 96;
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });

    const timer = setTimeout(() => {
      const firstInput = target.querySelector('input');
      firstInput?.focus();
    }, 450);

    return () => clearTimeout(timer);
  }, [location.hash]);

  const handleScrollToApply = () => {
    const el = document.getElementById('apply');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        const firstInput = el.querySelector('input');
        firstInput?.focus();
      }, 450);
    }
  };

  const benefits = [
    { label: 'تجهيز الأوراق', icon: FileText },
    { label: 'مصاريف التقديم', icon: CreditCard },
    { label: 'تكاليف السفر', icon: Plane },
    { label: 'تكاليف الدراسة', icon: GraduationCap },
    { label: 'الدعم خلال رحلة التقديم', icon: HeartHandshake },
  ];

  return (
    <Layout>
      <div dir="rtl" lang="ar" className="min-h-screen bg-[#FAF9FD] text-[#2C2C2C] pb-20">
        {/* 1. Hero Section */}
        <section className="pt-28 sm:pt-36 pb-12 sm:pb-16 px-4 sm:px-6 max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5B3FD0]/10 text-[#5B3FD0] text-xs sm:text-sm font-semibold mb-6">
            <span>المنحة من EduNasser</span>
          </div>

          {/* Heading */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2C2C2C] tracking-tight leading-tight sm:leading-tight mb-6">
            فرصتك للدراسة في أوروبا بتمويل كامل
          </h1>

          {/* Paragraph */}
          <p className="text-base sm:text-lg text-[#707070] max-w-2xl mx-auto leading-relaxed mb-8">
            EduNasser بتفتح باب التقديم للطلاب المتفوقين للحصول على فرصة ممولة بالكامل للدراسة في أوروبا. من تجهيز الأوراق وحتى السفر والدراسة.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col items-center gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={handleScrollToApply}
              className="gap-2.5 px-8 py-3.5 text-base font-bold shadow-md hover:shadow-lg rounded-xl transition-all"
            >
              <span>قدم دلوقتي</span>
              <ArrowDown className="w-4 h-4" />
            </Button>
            <Link
              to="/scholarship/details"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5B3FD0] hover:text-[#4729b0] transition-colors py-1 px-2"
            >
              <span>اعرف أكتر عن المنحة</span>
              <ArrowLeft className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* 2. What the Scholarship Includes */}
        <section className="py-8 px-4 sm:px-6 max-w-4xl mx-auto mb-10">
          <div className="text-center mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C]">
              المنحة بتشمل إيه؟
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {benefits.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.label}
                  className="rounded-2xl border-2 border-gray-200 bg-white p-4 flex flex-col items-center text-center justify-center gap-3 transition-colors hover:border-[#5B3FD0]/30"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#5B3FD0]/10 text-[#5B3FD0] flex items-center justify-center shrink-0">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <span className="text-xs sm:text-sm font-bold text-[#2C2C2C]">
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        {/* 3. Apply Section */}
        <section id="apply" className="px-4 sm:px-6 max-w-2xl mx-auto scroll-mt-24">
          <ScholarshipForm />
        </section>
      </div>
    </Layout>
  );
}
